/**
 * editStore.js — captions and annotations that survive a refresh.
 *
 * Edits are typed by hand, page by page, and until now lived only in React
 * state: a refresh, a crashed tab or a closed laptop lost every one of them. On
 * a 23-page report that is an afternoon.
 *
 * Two rules shape the whole module, and both come from the same failure this
 * codebase keeps producing — a stage that silently picks the wrong input and
 * returns a confident, wrong result:
 *
 *  1. **Edits belong to ONE case, and are never restored into another.** The case
 *     id is in the storage key AND inside the saved envelope, and load() checks
 *     both. A caption reading "Genioplasty osteotomy guide" appearing on a
 *     different patient's report is exactly the kind of quiet wrongness that is
 *     worse than losing the work.
 *  2. **Nothing here may throw.** It runs on every keystroke and on mount. A
 *     corrupt entry, a foreign key, a browser with storage disabled — each
 *     returns a reason, never an exception that unmounts the tool.
 *
 * Storage is injected so this is testable in node without a DOM.
 */

/** Bump when the saved shape changes; older envelopes are dropped, not guessed at. */
export const STORE_VERSION = 1

const PREFIX = 'o3d.caseReport.edits'

/**
 * Refuse to hold more than this. localStorage is ~5 MB for the WHOLE origin,
 * shared with everything else the portal keeps, and a silent QuotaExceededError
 * mid-session would look exactly like "my edits stopped saving".
 */
export const MAX_BYTES = 512 * 1024

/**
 * The key for a case, or null when there is no case identity.
 *
 * Null is deliberate: persisting unnamed work under a shared key would merge two
 * different operators' cases into one bucket. No identity → no persistence, and
 * the UI says so.
 */
export function storageKey(caseId) {
  const id = String(caseId ?? '').trim()
  return id ? `${PREFIX}.v${STORE_VERSION}:${id}` : null
}

/** Is this a plausible edit record? Malformed entries are dropped, not repaired. */
function validEdit(e) {
  return !!e
    && typeof e === 'object'
    && (e.captions == null || (typeof e.captions === 'object' && !Array.isArray(e.captions)))
    && (e.annotations == null || Array.isArray(e.annotations))
}

function normalise(e) {
  return {
    captions: e.captions ?? {},
    annotations: (e.annotations ?? []).filter((a) => a && typeof a === 'object' && a.kind),
    hidden: !!e.hidden,
  }
}

/**
 * @returns {{edits: object, savedAt: string|null, dropped: number} | null}
 *   null when there is nothing to restore, or when what is stored does not
 *   belong to this case. `dropped` counts entries thrown away as malformed — it
 *   is reported so a half-restored set is visible rather than assumed complete.
 */
export function loadEdits(caseId, storage = globalThis.localStorage) {
  const key = storageKey(caseId)
  if (!key || !storage) return null
  let raw
  try { raw = storage.getItem(key) } catch { return null }   // storage disabled
  if (!raw) return null

  let env
  try { env = JSON.parse(raw) } catch { return null }        // corrupt: drop quietly
  if (!env || env.v !== STORE_VERSION) return null

  // Defence in depth: the key already scopes by case, but a copied or migrated
  // entry could carry the wrong payload, and restoring one patient's captions
  // onto another's report is worse than losing them.
  if (String(env.caseId ?? '') !== String(caseId ?? '').trim()) return null
  if (!env.edits || typeof env.edits !== 'object' || Array.isArray(env.edits)) return null

  const edits = {}
  let dropped = 0
  for (const [k, e] of Object.entries(env.edits)) {
    if (validEdit(e)) edits[k] = normalise(e)
    else dropped += 1
  }
  return { edits, savedAt: env.savedAt ?? null, dropped }
}

/**
 * @returns {{ok: boolean, reason?: string, bytes?: number}} — never throws.
 *   A refusal is returned so the UI can say "not saved" instead of implying it
 *   was.
 */
export function saveEdits(caseId, edits, storage = globalThis.localStorage, now = null) {
  const key = storageKey(caseId)
  if (!key) return { ok: false, reason: 'no-case-id' }
  if (!storage) return { ok: false, reason: 'no-storage' }

  // Nothing to keep: remove the entry rather than leaving a stale one that a
  // later session would restore over a deliberately cleared slate.
  if (!edits || !Object.keys(edits).length) {
    try { storage.removeItem(key) } catch { /* ignore */ }
    return { ok: true, bytes: 0 }
  }

  const payload = JSON.stringify({
    v: STORE_VERSION,
    caseId: String(caseId).trim(),
    savedAt: now ?? new Date().toISOString(),
    edits,
  })
  if (payload.length > MAX_BYTES) return { ok: false, reason: 'too-large', bytes: payload.length }

  try { storage.setItem(key, payload) } catch { return { ok: false, reason: 'quota' } }
  return { ok: true, bytes: payload.length }
}

export function clearEdits(caseId, storage = globalThis.localStorage) {
  const key = storageKey(caseId)
  if (!key || !storage) return false
  try { storage.removeItem(key); return true } catch { return false }
}

/**
 * Every case with saved edits, newest first — so an operator can be told the
 * work exists rather than discovering it only by retyping the same case id.
 */
export function listSavedCases(storage = globalThis.localStorage) {
  if (!storage) return []
  const out = []
  let n = 0
  try { n = storage.length } catch { return [] }
  for (let i = 0; i < n; i++) {
    let k
    try { k = storage.key(i) } catch { continue }
    if (!k || !k.startsWith(`${PREFIX}.v${STORE_VERSION}:`)) continue
    try {
      const env = JSON.parse(storage.getItem(k))
      if (env?.v === STORE_VERSION && env.caseId) {
        out.push({ caseId: env.caseId, savedAt: env.savedAt ?? null, pages: Object.keys(env.edits ?? {}).length })
      }
    } catch { /* skip corrupt */ }
  }
  return out.sort((a, b) => String(b.savedAt ?? '').localeCompare(String(a.savedAt ?? '')))
}
// ── draft inputs ─────────────────────────────────────────────────────────────
//
// `picks` (which mesh fills an ambiguous slot) and `parts` (which mesh or
// polygroup is a printed guide) are DOCUMENT-level answers the operator gives
// before the draft is built, not page edits. They get their own key rather than
// a slot inside the edits envelope: `normalise()` there returns a fixed field
// list, so anything added to it is silently dropped on reload — the trap this
// avoids by not going near it.
//
// Same discipline as the edits store: keyed by case, and the payload re-checks
// the case on load, because restoring one patient's part declarations onto
// another's report would recolour a clinical document without saying so.

const DRAFT_PREFIX = 'o3d.caseReport.draft'

export function draftKey(caseId) {
  const id = String(caseId ?? '').trim()
  return id ? `${DRAFT_PREFIX}.v${STORE_VERSION}:${id}` : null
}

/** @returns {{ok: boolean, reason?: string}} — never throws. */
export function saveDraftInputs(caseId, inputs, storage = globalThis.localStorage) {
  const key = draftKey(caseId)
  if (!key) return { ok: false, reason: 'no-case-id' }
  if (!storage) return { ok: false, reason: 'no-storage' }
  const picks = inputs?.picks ?? {}
  const parts = inputs?.parts ?? {}
  if (!Object.keys(picks).length && !Object.keys(parts).length) {
    try { storage.removeItem(key) } catch { /* ignore */ }
    return { ok: true }
  }
  try {
    storage.setItem(key, JSON.stringify({ v: STORE_VERSION, caseId: String(caseId).trim(), picks, parts }))
    return { ok: true }
  } catch (e) { return { ok: false, reason: String(e?.name ?? e) } }
}

/** @returns {{picks: object, parts: object} | null} */
export function loadDraftInputs(caseId, storage = globalThis.localStorage) {
  const key = draftKey(caseId)
  if (!key || !storage) return null
  let raw
  try { raw = storage.getItem(key) } catch { return null }
  if (!raw) return null
  let env
  try { env = JSON.parse(raw) } catch { return null }
  if (!env || env.v !== STORE_VERSION) return null
  if (String(env.caseId ?? '') !== String(caseId ?? '').trim()) return null
  const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {})
  return { picks: obj(env.picks), parts: obj(env.parts) }
}

// ── the page basket (page mode, ADR 0015) ───────────────────────────────────
//
// The ordered list of pages an operator is building a report from. Its own key,
// for the same reason the draft inputs have one: `normalise()` in the edits
// envelope returns a fixed field list and would drop it on reload.
//
// Keyed by case, and the payload re-checks the case on load — a basket restored
// onto another patient's report would lay that patient's pages out under this
// one's name. The template travels inside the payload because a basket belongs
// to exactly one template.

const BASKET_PREFIX = 'o3d.caseReport.basket'

export function basketKey(caseId) {
  const id = String(caseId ?? '').trim()
  return id ? `${BASKET_PREFIX}.v${STORE_VERSION}:${id}` : null
}

/** @returns {{ok: boolean, reason?: string}} — never throws. */
export function saveBasket(caseId, basket, storage = globalThis.localStorage) {
  const key = basketKey(caseId)
  if (!key) return { ok: false, reason: 'no-case-id' }
  if (!storage) return { ok: false, reason: 'no-storage' }
  if (!basket?.template) return { ok: false, reason: 'no-template' }
  try {
    storage.setItem(key, JSON.stringify({
      v: STORE_VERSION, caseId: String(caseId).trim(),
      template: basket.template, seq: basket.seq, entries: basket.entries ?? [],
    }))
    return { ok: true }
  } catch (e) { return { ok: false, reason: String(e?.name ?? e) } }
}

/**
 * @returns {{basket: object, dropped: number} | null}
 *   null for no basket, another case's, another version's, or unreadable.
 *   Malformed entries are DROPPED and counted, never repaired — a guessed
 *   typeId would put a page in the report nobody chose.
 */
export function loadBasket(caseId, storage = globalThis.localStorage) {
  const key = basketKey(caseId)
  if (!key || !storage) return null
  let raw
  try { raw = storage.getItem(key) } catch { return null }
  if (!raw) return null
  let env
  try { env = JSON.parse(raw) } catch { return null }
  if (!env || env.v !== STORE_VERSION) return null
  if (String(env.caseId ?? '') !== String(caseId ?? '').trim()) return null
  if (typeof env.template !== 'string' || !env.template) return null

  const seen = new Set()
  const entries = []
  let dropped = 0
  for (const e of Array.isArray(env.entries) ? env.entries : []) {
    const ok = e && typeof e === 'object'
      && typeof e.instanceId === 'string' && /^i\d+$/.test(e.instanceId)
      && typeof e.typeId === 'string' && e.typeId
      && !seen.has(e.instanceId)
    if (!ok) { dropped += 1; continue }
    seen.add(e.instanceId)
    const obj = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {})
    // A generic page's own inputs: the meshes it shows (ids only) and its view.
    // Anything else in the stored object is dropped, not trusted.
    const raw = obj(e.inputs)
    const inputs = {}
    if (Array.isArray(raw.meshes)) inputs.meshes = raw.meshes.filter((x) => typeof x === 'string')
    if (typeof raw.view === 'string') inputs.view = raw.view
    entries.push({ instanceId: e.instanceId, typeId: e.typeId, picks: obj(e.picks), inputs })
  }
  // 🔴 `seq` is floored at the highest id actually present. A stored seq BELOW
  // an existing id would reissue that id to the next page added, which would
  // then adopt the other page's edits. Raising it can only make ids safer; it
  // is the one value this function will change, and only upward.
  const highest = entries.reduce((m, e) => Math.max(m, Number(e.instanceId.slice(1))), 0)
  const seq = Math.max(Number.isInteger(env.seq) ? env.seq : 0, highest)
  return { basket: { template: env.template, seq, entries }, dropped }
}
